import { useCallback, useEffect, useRef, useState } from 'react'
import { ArrowRight, ChevronLeft, ChevronRight, Newspaper } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { newsKey } from '@/features/news/lib'
import type { News } from '@/lib/graphql/types'
import { cn } from '@/lib/utils'

/**
 * Carosello "News & comunicazioni" della Home.
 *
 * Scroll orizzontale con frecce ‹/›: niente barra di scroll visibile.
 * Su desktop ogni card occupa `(100% - 2*gap) / 3` → **massimo 3 card
 * visibili**. Su mobile ~80vw così la successiva si intravede.
 * Click sulle frecce → scroll di una "pagina" (= larghezza del viewport
 * del carosello).
 */
export function NewsCarousel({ items }: { items: News[] }) {
  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-baseline justify-between">
        <h2 className="text-lg font-bold text-foreground">News e comunicazioni</h2>
        <Link
          to="/news"
          className="inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline"
        >
          Vedi tutte <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Newspaper className="h-5 w-5" />}
          title="Nessuna news al momento"
          description="Le comunicazioni più recenti compariranno qui."
        />
      ) : (
        <CarouselTrack count={items.length}>
          {items.map((item) => (
            <div
              key={newsKey(item)}
              className="w-[80vw] shrink-0 snap-start sm:w-[55vw] md:w-[calc((100%-2rem)/3)]"
            >
              <NewsCard item={item} />
            </div>
          ))}
        </CarouselTrack>
      )}
    </section>
  )
}

// =====================================================================
// Carousel track con frecce navigabili
// =====================================================================

function CarouselTrack({
  children,
  count,
}: {
  children: React.ReactNode
  count: number
}) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [canPrev, setCanPrev] = useState(false)
  const [canNext, setCanNext] = useState(false)

  // Aggiorna lo stato dei bottoni in base alla posizione di scroll.
  const updateNav = useCallback(() => {
    const el = trackRef.current
    if (!el) return
    const { scrollLeft, scrollWidth, clientWidth } = el
    setCanPrev(scrollLeft > 4)
    setCanNext(scrollLeft + clientWidth < scrollWidth - 4)
  }, [])

  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    updateNav()
    el.addEventListener('scroll', updateNav, { passive: true })
    const ro = new ResizeObserver(updateNav)
    ro.observe(el)
    return () => {
      el.removeEventListener('scroll', updateNav)
      ro.disconnect()
    }
  }, [updateNav, count])

  function scrollBy(direction: 1 | -1) {
    const el = trackRef.current
    if (!el) return
    // Scroll di ~90% della larghezza visibile (sembra naturale, lascia
    // un pelo di overlap con la card precedente).
    const delta = el.clientWidth * 0.9 * direction
    el.scrollBy({ left: delta, behavior: 'smooth' })
  }

  return (
    <div className="relative">
      {/* Track scrollabile, scrollbar nascosta */}
      <div
        ref={trackRef}
        className="-mx-4 flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-4 pb-1 md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
        }}
      >
        {children}
      </div>

      {/* Freccia sinistra */}
      <CarouselArrow
        direction="left"
        visible={canPrev}
        onClick={() => scrollBy(-1)}
      />

      {/* Freccia destra */}
      <CarouselArrow
        direction="right"
        visible={canNext}
        onClick={() => scrollBy(1)}
      />
    </div>
  )
}

function CarouselArrow({
  direction,
  visible,
  onClick,
}: {
  direction: 'left' | 'right'
  visible: boolean
  onClick: () => void
}) {
  const Icon = direction === 'left' ? ChevronLeft : ChevronRight
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={direction === 'left' ? 'Card precedente' : 'Card successiva'}
      tabIndex={visible ? 0 : -1}
      className={cn(
        'absolute top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-md transition-all md:flex',
        'hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        direction === 'left' ? '-left-3' : '-right-3',
        visible
          ? 'opacity-100'
          : 'pointer-events-none opacity-0',
      )}
    >
      <Icon className="h-5 w-5" />
    </button>
  )
}

// =====================================================================
// Card
// =====================================================================

function NewsCard({ item }: { item: News }) {
  const key = newsKey(item)
  const date = item.datetime
    ? new Date(item.datetime).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    : ''
  return (
    <Link to={`/news/${key}`} className="block h-full">
      <Card interactive className="flex h-full flex-col overflow-hidden p-0">
        {item.img ? (
          <img
            src={item.img}
            alt=""
            loading="lazy"
            className="h-32 w-full object-cover"
          />
        ) : (
          <div className="h-32 w-full bg-muted" aria-hidden />
        )}
        <div className="flex flex-1 flex-col gap-2 p-4">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground">
            {item.title}
          </h3>
          {item.incipit && (
            <p className="line-clamp-2 text-xs text-muted-foreground">{item.incipit}</p>
          )}
          {date && (
            <p className="mt-auto pt-1 text-[11px] uppercase tracking-wider text-muted-foreground">
              {date}
            </p>
          )}
        </div>
      </Card>
    </Link>
  )
}
